import { useEffect, useState } from 'react';
import { motion } from 'motion/react';
import type { CartaoIngresso } from '../types';

type Campo = 'nome' | 'cpf' | 'whatsapp';

interface CartaoIdentificacaoProps {
  indice: number;
  cartao: CartaoIngresso;
  mostrarSouEu: boolean;
  erros: { nome?: string; cpf?: string; whatsapp?: string };
  /** A pessoa tentou pagar: aí os erros aparecem mesmo sem sair do campo. */
  forcarErros?: boolean;
  /** Cada toque no Pagar incompleto soma 1: o cartão dá o "tremidinho". */
  tremer?: number;
  onChange: (patch: Partial<CartaoIngresso>) => void;
  onSouEu: () => void;
  onBlurNome?: () => void;
  onBlurCpf?: () => void;
  onBlurWhatsapp?: () => void;
}

interface CampoProps {
  id: string;
  label: string;
  valor: string;
  erro?: string;
  inputMode: 'text' | 'numeric' | 'tel';
  placeholder: string;
  autoComplete: string;
  onChange: (v: string) => void;
  onBlur: () => void;
}

// Campo com moldura, 52 px de altura, teclado certo e erro embaixo.
function CampoIngresso({
  id,
  label,
  valor,
  erro,
  inputMode,
  placeholder,
  autoComplete,
  onChange,
  onBlur,
}: CampoProps) {
  return (
    <div className="min-w-0">
      <label htmlFor={id} className="block text-[12.5px] font-semibold text-foreground-700">
        {label}
      </label>
      <input
        id={id}
        name={id}
        type={inputMode === 'text' ? 'text' : 'tel'}
        inputMode={inputMode}
        value={valor}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        placeholder={placeholder}
        autoComplete={autoComplete}
        className={`mt-1 box-border h-[52px] w-full rounded-[12px] border bg-background-50 px-3 text-[15px] text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-500 ${
          erro ? 'border-danger-400 ring-2 ring-danger-100' : 'border-background-300'
        }`}
      />
      {erro && (
        <p className="mt-1 text-[12.5px] leading-snug text-danger-600" role="alert" data-erro="true">
          {erro}
        </p>
      )}
    </div>
  );
}

// Um cartão por ingresso: nome, CPF e WhatsApp de quem vai usar.
export default function CartaoIdentificacao({
  indice,
  cartao,
  mostrarSouEu,
  erros,
  forcarErros = false,
  tremer = 0,
  onChange,
  onSouEu,
  onBlurNome,
  onBlurCpf,
  onBlurWhatsapp,
}: CartaoIdentificacaoProps) {
  const [tocados, setTocados] = useState<Record<Campo, boolean>>({
    nome: false,
    cpf: false,
    whatsapp: false,
  });

  const saiu = (campo: Campo) => setTocados((t) => ({ ...t, [campo]: true }));
  const erroDe = (campo: Campo) => ((tocados[campo] || forcarErros) ? erros[campo] : undefined);

  // Tremidinho horizontal (±4 px, 2 vezes, 200 ms) quando falta preencher.
  const [sacudir, setSacudir] = useState(false);
  useEffect(() => {
    if (!tremer) return;
    if (!erros.nome && !erros.cpf && !erros.whatsapp) return;
    setSacudir(true);
    const t = window.setTimeout(() => setSacudir(false), 220);
    return () => window.clearTimeout(t);
  }, [tremer, erros.nome, erros.cpf, erros.whatsapp]);

  return (
    <motion.div
      animate={sacudir ? { x: [0, -4, 4, -4, 4, 0] } : { x: 0 }}
      transition={{ duration: 0.2, ease: 'easeInOut' }}
      className="rounded-[16px] bg-background-50 p-4 shadow-pcard"
    >
      <div className="mb-3 flex items-center justify-between gap-3">
        <h3 className="text-[15px] font-extrabold text-foreground-950">Ingresso {indice + 1}</h3>
        {mostrarSouEu && (
          <button
            type="button"
            onClick={onSouEu}
            className="cursor-pointer whitespace-nowrap rounded-full bg-primary-50 px-3 py-1.5 text-[13px] font-semibold text-primary-600 transition-colors hover:bg-primary-100"
          >
            Sou eu
          </button>
        )}
      </div>

      <div className="space-y-3">
        <CampoIngresso
          id={`ingresso-${indice}-nome`}
          label="Nome completo"
          valor={cartao.nome}
          erro={erroDe('nome')}
          inputMode="text"
          placeholder="Como está no documento"
          autoComplete="name"
          onChange={(v) => onChange({ nome: v })}
          onBlur={() => {
            saiu('nome');
            onBlurNome?.();
          }}
        />

        <CampoIngresso
          id={`ingresso-${indice}-cpf`}
          label="CPF"
          valor={cartao.cpf}
          erro={erroDe('cpf')}
          inputMode="numeric"
          placeholder="000.000.000-00"
          autoComplete="off"
          onChange={(v) => onChange({ cpf: v })}
          onBlur={() => {
            saiu('cpf');
            onBlurCpf?.();
          }}
        />

        <CampoIngresso
          id={`ingresso-${indice}-whatsapp`}
          label="WhatsApp"
          valor={cartao.whatsapp}
          erro={erroDe('whatsapp')}
          inputMode="numeric"
          placeholder="(00) 00000-0000"
          autoComplete="tel"
          onChange={(v) => onChange({ whatsapp: v })}
          onBlur={() => {
            saiu('whatsapp');
            onBlurWhatsapp?.();
          }}
        />
      </div>
    </motion.div>
  );
}